import * as Sentry from '@sentry/react';
import env from 'config';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

interface ISessionSession {
  wsID?: string;
  machineID?: string;
}

// locally scoped mutable session info
export const sentrySession: ISessionSession = {
  wsID: undefined,
  machineID: undefined,
};

export const updateSentrySession = (
  session: Partial<typeof sentrySession>
): ISessionSession => Object.assign(sentrySession, session);

// * Automated tracing is currently disabled on the frontend until we get a better understanding of quota usage
// * Sentry currently serves to report on crashes / errors
export const initSentry = () =>
  Sentry.init({
    enabled: env.integrations.sentry.enabled,
    dsn: env.integrations.sentry.dsn,
    environment: env.integrations.sentry.environment,
    release: env.getBuildStamp(),
    tracesSampleRate: env.integrations.sentry.trace_sample_rate || 0, // currently 0 for all environments

    sendDefaultPii: true,

    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracePropagationTargets: [env.server_url],

    // auto-tag wsID and machineID to all events / transactions for ease of filtering
    beforeSendTransaction(tx) {
      const { wsID, machineID } = sentrySession;

      const updatedTx = {
        ...tx,
        tags: {
          ...tx.tags,
          wsID,
          machineID,
        },
      };

      if (!updatedTx.spans?.length) {
        return updatedTx;
      }

      for (const span of updatedTx.spans) {
        span.data = { ...(span.data || {}) };

        if (wsID) {
          span.data['wsID'] = wsID;
        }

        if (machineID) {
          span.data['machineID'] = machineID;
        }
      }

      return updatedTx;
    },

    // auto-tag wsID and machineID to all errors
    beforeSend(event) {
      const { wsID, machineID } = sentrySession;

      const updatedEvent = {
        ...event,
        tags: {
          ...event.tags,
          wsID,
          machineID,
        },
      };

      return updatedEvent;
    },
  });
